import { usePlatformBranding } from '../lib/branding'

const heightClass = {
  sm: 'h-7',
  md: 'h-10',
  // Sized for the AuthLayout's main hero logo on the SignIn /
  // SignUp split-screen surface (the only caller using `lg`).
  // h-16 (64 px) reads as a comfortable hero mark — large enough to
  // anchor the brand panel, small enough to leave the heading
  // ("Built For Teams That Run On SOPs.") as the dominant element.
  // Iterated 2026-06-08: started at h-16, bumped to h-20, fine-
  // tuned to h-[79px], then reduced back to h-16 per request.
  lg: 'h-16',
  xl: 'h-16',
  none: '', // lets the caller fully control height via className
}

/**
 * Brand logo, sourced dynamically from the Super Admin → Settings
 * upload (via `/api/v1/platform/branding`) with a graceful fall-back
 * to the bundled `/logo.png` asset.
 *
 * Why dynamic:
 *   When a Super Admin uploads a new platform logo, every consumer
 *   of <Logo> across the app — sidebars, auth screens, error pages,
 *   maintenance screen — re-renders with the new URL without a
 *   redeploy. Before this change, `src` was hardcoded to
 *   `/logo.png`, so the upload only affected email templates.
 *
 * Why a fall-back:
 *   - Before the first fetch completes, `branding.data` is undefined
 *     and we'd otherwise render `<img src="">` (broken icon flash).
 *   - When the API is down / errors out, the bundled PNG from
 *     `public/logo.png` is the worst-case visual (same look as
 *     before this feature shipped).
 *   - When no logo has been uploaded yet, `logoUrl` is the empty
 *     string and the same fallback applies.
 *
 * `alt` reads from the same branding payload so a rename in Settings
 * also propagates to the alt attribute (screen-reader brand consistency).
 */
export function Logo({ size = 'md', className }) {
  const { data: branding } = usePlatformBranding()
  const src = branding?.logoUrl || '/logo.png'
  const alt = branding?.name || 'TaskMagix'
  return (
    <img
      src={src}
      alt={alt}
      className={`${heightClass[size]} w-auto select-none ${className ?? ''}`}
      draggable={false}
    />
  )
}
